// Tenant hub Support page.
// All content (contact person + FAQ) is pulled 1-op-1 from the super-admin
// managed global config. Nothing is hardcoded — if the super-admin leaves a
// field empty, that field simply disappears from the hub. FAQ items carry a
// role tag (viewer | creator | manager); the hub shows only the ones that
// match the current user's role, with owner mapped to the manager-list.
import { useEffect, useMemo, useState } from "react";
import { Search, Phone, Mail, UserRound, ChevronDown } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useGlobalConfig } from "@/contexts/GlobalConfigContext";
import { Input } from "@/components/ui/input";
import { fileUrl } from "@/lib/api";

const stripHtml = (s = "") => s.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ");

// Owner always sees the manager-tier FAQ (highest trust). Everything else
// matches on its own role name.
const faqRoleFor = (role) => (role === "owner" ? "manager" : (role || "viewer"));

export default function SupportHub({ role }) {
  const { t } = useI18n();
  const { config, refresh } = useGlobalConfig();
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  // Always refetch when the user navigates to Support — guarantees that an
  // admin change in Super Admin lands on the hub without a hard refresh.
  useEffect(() => { refresh?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveRole = faqRoleFor(role);
  const items = useMemo(() => {
    const all = config?.faq || [];
    return all.filter((f) => (f.role || "viewer") === effectiveRole);
  }, [config, effectiveRole]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => `${it.question} ${stripHtml(it.answer)}`.toLowerCase().includes(q));
  }, [items, query]);

  const support = config?.support || {};
  const hasContact = Boolean((support.name || "").trim());

  return (
    <div className="min-h-full bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-16">
        <h1
          className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1a1a1a] mb-2"
          data-testid="support-title"
        >
          {t("support.title")}
        </h1>
        <p className="text-sm text-gray-500 mb-10">{t("support.sub")}</p>

        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-10 items-start">
          <aside className="md:sticky md:top-6">
            {hasContact ? (
              <ContactCard t={t} support={support} />
            ) : (
              <div
                data-testid="support-contact-empty"
                className="rounded-[var(--global-radius-xl)] bg-[#f3f3f1] p-5 text-xs text-gray-500 leading-relaxed"
              >
                Je contactpersoon wordt binnenkort door Noordzuid ingesteld.
              </div>
            )}
          </aside>

          <section
            data-testid="support-faq"
            className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white p-4 sm:p-6 md:p-8"
          >
            <h2 className="font-display text-2xl font-medium text-[#1a1a1a] mb-4">
              {t("support.faq_title")}
            </h2>
            <div className="relative mb-6">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.75} />
              <Input
                data-testid="support-faq-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("support.faq_search_placeholder")}
                className="h-11 bg-white border-gray-200 pl-9"
              />
            </div>

            {filtered.length === 0 ? (
              <div className="py-12 text-center text-sm text-gray-500">
                {items.length === 0 ? t("support.faq_empty") : t("support.faq_no_results").replace("{query}", query)}
              </div>
            ) : (
              <div className="divide-y divide-gray-100 border-y border-gray-100">
                {filtered.map((it) => {
                  const open = openId === it.id;
                  return (
                    <div key={it.id} className="py-1">
                      <button
                        type="button"
                        data-testid={`support-faq-item-${it.id}`}
                        onClick={() => setOpenId(open ? null : it.id)}
                        className="w-full flex items-center justify-between gap-4 py-3 text-left"
                      >
                        <span className="text-[15px] text-[#1a1a1a] font-medium">{it.question}</span>
                        <ChevronDown className={`w-4 h-4 shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.75} />
                      </button>
                      {open && (
                        <div
                          data-testid={`support-faq-answer-${it.id}`}
                          className="pb-5 pr-6 text-[14px] text-[#1a1a1a]/80 leading-relaxed prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: it.answer }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function ContactCard({ t, support }) {
  const phone = support.show_phone && (support.phone || "").trim() ? support.phone : "";
  const email = support.show_email && (support.email || "").trim() ? support.email : "";
  const avatar = support.avatar_file_id ? fileUrl(support.avatar_file_id) : null;

  return (
    <div data-testid="support-contact-card" className="rounded-[var(--global-radius-xl)] bg-[#f3f3f1] p-5">
      <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-3">{t("support.contact_title")}</div>
      <p className="text-xs text-gray-500 mb-4">{t("support.contact_sub")}</p>

      {avatar && (
        <div className="mb-4">
          <img src={avatar} alt={support.name} className="w-16 h-16 rounded-full object-cover border border-white" />
        </div>
      )}

      <ContactRow icon={UserRound} label={t("support.contact_person_label")} value={support.name} />
      {phone && <ContactRow icon={Phone} label={t("support.contact_phone_label")} value={phone} href={`tel:${phone.replace(/\s+/g, "")}`} />}
      {email && <ContactRow icon={Mail} label={t("support.contact_email_label")} value={email} href={`mailto:${email}`} />}
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href }) {
  const content = (
    <>
      <div className="w-8 h-8 rounded-[var(--global-radius)] bg-white flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#1a1a1a]" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] text-gray-500 leading-tight">{label}</div>
        <div className="text-sm text-[#1a1a1a] truncate">{value}</div>
      </div>
    </>
  );
  return (
    <div className="flex items-center gap-3 py-2.5">
      {href ? (
        <a href={href} className="flex items-center gap-3 flex-1 min-w-0 hover:text-[var(--bh-accent,#fe7f57)] transition-colors">{content}</a>
      ) : (
        <div className="flex items-center gap-3 flex-1 min-w-0">{content}</div>
      )}
    </div>
  );
}
